/**
 * ManualAddressQuestion Component
 * Fallback for when address lookup fails
 * Captures address details in free-form inputs
 */

import { useState, useEffect } from 'react'

interface ManualAddressValue {
  line1: string
  line2: string
  line3: string
  town: string
  county: string
  postcode: string
  addressEntryMethod: 'MANUAL-ENTRY'
}

interface ManualAddressQuestionProps {
  questionId: string
  postcode: string
  value: ManualAddressValue | object
  onChange: (value: ManualAddressValue) => void
  required?: boolean
  error?: string
}

const ManualAddressQuestion = ({
  questionId,
  postcode,
  value,
  onChange,
  required = false,
  error
}: ManualAddressQuestionProps) => {
  const [addressData, setAddressData] = useState<ManualAddressValue>({
    line1: '',
    line2: '',
    line3: '',
    town: '',
    county: '',
    postcode: postcode,
    addressEntryMethod: 'MANUAL-ENTRY'
  })

  // Initialize from value if provided (only runs once on mount)
  useEffect(() => {
    if (value && typeof value === 'object' && 'line1' in value) {
      setAddressData(value as ManualAddressValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally only run on mount to initialize from props
  }, [])

  const handleFieldChange = (field: keyof ManualAddressValue, fieldValue: string) => {
    const updated = {
      ...addressData,
      [field]: fieldValue
    }
    setAddressData(updated)
    onChange(updated)
  }

  const inputClassName = `
    w-full px-4 py-3 text-base border-2 rounded-lg transition-all duration-200
    focus:outline-none focus:ring-2 focus:ring-primary-green focus:ring-opacity-50
    border-gray-300 bg-white focus:border-primary-green
  `

  return (
    <div className="w-full max-w-2xl space-y-4">
      {/* Address Line 1 */}
      <div>
        <label htmlFor={`${questionId}-line1`} className="block text-sm font-medium text-gray-700 mb-2">
          Address Line 1 *
        </label>
        <input
          type="text"
          id={`${questionId}-line1`}
          value={addressData.line1}
          onChange={(e) => handleFieldChange('line1', e.target.value)}
          placeholder="House number and street name"
          required={required}
          autoFocus
          className={inputClassName}
        />
      </div>

      {/* Address Line 2 */}
      <div>
        <label htmlFor={`${questionId}-line2`} className="block text-sm font-medium text-gray-700 mb-2">
          Address Line 2
        </label>
        <input
          type="text"
          id={`${questionId}-line2`}
          value={addressData.line2}
          onChange={(e) => handleFieldChange('line2', e.target.value)}
          placeholder="Apartment, suite, floor, etc. (optional)"
          className={inputClassName}
        />
      </div>

      {/* Address Line 3 */}
      <div>
        <label htmlFor={`${questionId}-line3`} className="block text-sm font-medium text-gray-700 mb-2">
          Address Line 3
        </label>
        <input
          type="text"
          id={`${questionId}-line3`}
          value={addressData.line3}
          onChange={(e) => handleFieldChange('line3', e.target.value)}
          placeholder="Additional address information (optional)"
          className={inputClassName}
        />
      </div>

      {/* Town/City */}
      <div>
        <label htmlFor={`${questionId}-town`} className="block text-sm font-medium text-gray-700 mb-2">
          Town/City *
        </label>
        <input
          type="text"
          id={`${questionId}-town`}
          value={addressData.town}
          onChange={(e) => handleFieldChange('town', e.target.value)}
          placeholder="Town or city"
          required={required}
          className={inputClassName}
        />
      </div>

      {/* County */}
      <div>
        <label htmlFor={`${questionId}-county`} className="block text-sm font-medium text-gray-700 mb-2">
          County
        </label>
        <input
          type="text"
          id={`${questionId}-county`}
          value={addressData.county}
          onChange={(e) => handleFieldChange('county', e.target.value)}
          placeholder="County (optional)"
          className={inputClassName}
        />
      </div>

      {/* Postcode (read-only, pre-filled) */}
      <div>
        <label htmlFor={`${questionId}-postcode`} className="block text-sm font-medium text-gray-700 mb-2">
          Postcode *
        </label>
        <input
          type="text"
          id={`${questionId}-postcode`}
          value={addressData.postcode}
          readOnly
          className={`${inputClassName} bg-gray-50 cursor-not-allowed`}
        />
      </div>

      {/* Error Message */}
      {error && (
        <div
          id={`${questionId}-error`}
          className="mt-2 text-sm text-red-600 flex items-center gap-1"
          role="alert"
        >
          <svg
            className="w-4 h-4"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {error}
        </div>
      )}
    </div>
  )
}

export default ManualAddressQuestion
